import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region types
import {
  AutoCompleteExtraProps,
  CompactEditableTableFieldProps,
  FormFieldDefinitionType,
  FormHandles,
} from "models/propTypes/genericFormProps";
import { TranslationKeys } from "translations";
import { FormEnums } from "models/enums";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { TimeReportingFormTypes } from "models/formTypes";
import { MonthlyFormViewProps } from "models/propTypes/timeReportingPropTypes";
import { MonthLongConfig, MonthShortConfig } from "utils/constants/dateConstants";
import { TimeReportType } from "models/enums/timeReporting";
//#endregion
//#region components
import { GenericForm } from "components/common";
import {
  IconField,
  MonthlyFormTableHeader,
  WeekHoursField,
  ProjectCategoryPickerField,
} from "components/timeReporting";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance } from "utils/helpers";

const MonthlyForm: ForwardRefRenderFunction<FormHandles, MonthlyFormViewProps> = (props, ref) => {
  const {
    monthLockEntries,
    weeksInMonth,
    dateRange,
    employeeId,
    isEditing,
    availableProjects,
    availableCategories,
    isCategorySelectionDisabled,
    onProjectSelect,
    onMonthTotalChange,
    onCategoryChanged,
    onRowRemove,
  } = props;

  const monthShort = React.useMemo(
    () => (dateRange?.start ? LocalizationHelperInstance.getFormattedDate(dateRange?.start, MonthShortConfig) : ""),
    [dateRange]
  );

  const getTableRowFields = () => {
    const rowfields = {
      type: {
        label: "",
        type: FormEnums.FormFieldType.CUSTOM,
        customField: <IconField></IconField>,
        classes: { formControl: cssExports.iconCell },
      },
      name: {
        label: "",
        controlLabel: dateRange?.start
          ? LocalizationHelperInstance.getFormattedDate(dateRange?.start, MonthLongConfig)
          : "",
        type: FormEnums.FormFieldType.CUSTOM,
        customField: isEditing ? (
          <ProjectCategoryPickerField
            getAvailableCategories={availableCategories}
            isCategorySelectionDisabled={isCategorySelectionDisabled}
            onCategoryChanged={onCategoryChanged}
          />
        ) : (
          <ProjectCategoryPickerField />
        ),
        readonly: isEditing ? (values: TimeReportingFormTypes.MonthlyTimeReportRow) => !values.isNew : true,
        icon: IconDefinitionConstants.WCalendarTime,
        classes: { formControl: cssExports.titleText },
      },
    } as FormFieldDefinitionType<TimeReportingFormTypes.MonthlyTimeReportRow>;

    weeksInMonth?.forEach((week) => {
      (rowfields as any)[week] = {
        label: "-",
        controlLabel: `${week} ${monthShort.toUpperCase()}`,
        rightAlign: true,
        // The month total column needs room while editing; at 2 the six columns would claim the full width.
        widthColumnCount: isEditing ? 1 : 2,
        type: FormEnums.FormFieldType.CUSTOM,
        customField: (
          <WeekHoursField
            employeeId={employeeId}
            weekDisplayText={week}
            monthStart={dateRange?.start}
            disableLink={isEditing}
          ></WeekHoursField>
        ),
      };
    });

    if (isEditing) {
      rowfields.monthTotal = {
        label: "-",
        controlLabel: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.MonthTotal),
        type: FormEnums.FormFieldType.NUMBER,
        rightAlign: true,
        widthColumnCount: 2,
        extraProps: { suffix: "h", decimalPoints: 2 },
        classes: { formField: cssExports.monthTotalField },
        disabled: (values: TimeReportingFormTypes.MonthlyTimeReportRow) => values.type !== TimeReportType.project,
        onChangeHandler: onMonthTotalChange,
        // Month navigation asks the form for unsaved changes, so a total typed just before the click must be in it.
        noDebounce: true,
      };
    }

    return rowfields;
  };

  return (
    <GenericForm<TimeReportingFormTypes.MonthlyTimeReport>
      formRef={ref}
      {...props}
      fields={{
        rows: {
          label: TranslationKeys.Settings.Units,
          controlLabel: "",
          type: FormEnums.FormFieldType.COMPACTEDITABLETABLE,
          innerFields: getTableRowFields(),
          extraProps: {
            headerRowFieldName: FormFieldConstants.TimeReporting.WeeklyTimeReport.HeaderRow,
            rowHideActions: (row: TimeReportingFormTypes.MonthlyTimeReportRow) => !isEditing || !row.isNew,
            externalHeader: (
              <MonthlyFormTableHeader
                innerFields={getTableRowFields()}
                locks={monthLockEntries}
                hideActions={!isEditing}
              ></MonthlyFormTableHeader>
            ),
            hideActions: !isEditing,
            disableRowSelection: !isEditing,
            activateAddedRow: isEditing,
            focusAddedRowField: FormFieldConstants.TimeReporting.MonthlyTimeReportRow.MonthTotal,
            onItemRemove: onRowRemove,
          } as CompactEditableTableFieldProps<TimeReportingFormTypes.MonthlyTimeReportRow>,
        },
        timeReportSuggestions: {
          label: "",
          type: FormEnums.FormFieldType.HORIONTALFIELDGROUP,
          widthColumnCount: 12,
          hidden: !isEditing,
          innerFields: {
            projectSuggestions: {
              label: TranslationKeys.TimeReporting.CreateNewProject,
              type: FormEnums.FormFieldType.AUTOCOMPLETE,
              extraProps: {
                items: availableProjects,
                onItemSelect: onProjectSelect,
              } as AutoCompleteExtraProps<TimeReportingFormTypes.MonthlyTimeReportRow>,
              classes: { formField: cssExports.compactField },
              widthColumnCount: 5,
            },
          },
        },
      }}
      inline={false}
      hideOptionalIndicator
    />
  );
};

export default forwardRef(MonthlyForm);
